/**
 * iPhone preview (2026-09-29). STATUS is an iPhone app. On a desktop browser the page
 * shows a device "stage" with the real app running in a same-origin iframe sized exactly
 * like an iPhone, so every media query, vh unit and safe area behaves as on the phone.
 * On a phone, in the native shell, or inside that iframe, the app renders normally.
 */

export interface DeviceSpec {
  id: DeviceId;
  name: string;
  /** Screen size in CSS points. */
  width: number;
  height: number;
  /** Safe-area insets in points (status bar / Dynamic Island, home indicator). */
  top: number;
  bottom: number;
  island: boolean;
  /** Screen corner radius in points (0 = square screen with a home button). */
  radius: number;
  /** Top and bottom bezel in points when deeper than the sides (home-button models). */
  bezelY?: number;
}

export type DeviceId = "se" | "16" | "16pro" | "promax";

export const DEVICES: readonly DeviceSpec[] = [
  { id: "se", name: "iPhone SE", width: 375, height: 667, top: 20, bottom: 0, island: false, radius: 0, bezelY: 64 },
  { id: "16", name: "iPhone 16", width: 393, height: 852, top: 59, bottom: 34, island: true, radius: 55 },
  { id: "16pro", name: "iPhone 16 Pro", width: 402, height: 874, top: 62, bottom: 34, island: true, radius: 62 },
  { id: "promax", name: "iPhone 16 Pro Max", width: 440, height: 956, top: 62, bottom: 34, island: true, radius: 62 },
];

export const DEFAULT_DEVICE: DeviceId = "16";
export const DEVICE_KEY = "status.preview.device";
/** A desktop-class browser: wide, with a precise pointer that can hover. */
export const STAGE_QUERY = "(min-width: 700px) and (hover: hover) and (pointer: fine)";

export type PreviewMode = "stage" | "device" | "app";

/** Pure. `stage` = desktop wrapper, `device` = the app inside the wrapper's iframe, `app` = phone or native. */
export function previewMode(env: { desktop: boolean; embedded: boolean; native: boolean }): PreviewMode {
  if (env.native) return "app";
  if (env.embedded) return "device";
  return env.desktop ? "stage" : "app";
}

export function deviceById(id: string | null | undefined): DeviceSpec {
  return DEVICES.find((d) => d.id === id) ?? DEVICES.find((d) => d.id === DEFAULT_DEVICE)!;
}

export function readDevice(): DeviceSpec {
  try {
    return deviceById(localStorage.getItem(DEVICE_KEY));
  } catch {
    return deviceById(null);
  }
}

export function saveDevice(id: DeviceId): void {
  try {
    localStorage.setItem(DEVICE_KEY, id);
  } catch {
    /* private mode */
  }
}

/** True when this document runs inside the STATUS preview stage (a same-origin parent). */
export function embeddedInStage(): boolean {
  try {
    return window.self !== window.top && window.parent.location.origin === window.location.origin;
  } catch {
    return false;
  }
}

/** Scale that fits a device of `outerHeight` px into a window `available` px tall. Pure. */
export function fitScale(outerHeight: number, available: number): number {
  if (outerHeight <= 0 || available <= 0) return 1;
  return Math.max(0.5, Math.min(1, available / outerHeight));
}
